'use client'

import { Navbar } from '@/components/layout/header/navbar'
import { TopBar } from '@/components/layout/header/topbar'
import { TopBarPromotion } from '@/components/layout/header/topbar-promotion'
import StoryMode from '@/components/story-mode/StoryMode'
import { useScrollDirectionNew } from '@/hooks/common/use-scroll-direction-new'
import { usePathname } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { useStoryModeData } from '@/providers/setting-provider'
import type { TStoryModePageType } from '@/types/story-mode'
import { useEffect, useRef, useState } from 'react'

interface HeaderProps {
  isShowTopBar?: boolean
  isShowNavbar?: boolean
  isShowPromotion?: boolean
  isMobile?: boolean
  isQrGalleryLayout?: boolean
}

export function Header({
  isShowTopBar = true,
  isShowNavbar = true,
  isShowPromotion = true,
  isMobile,
  isQrGalleryLayout = false,
}: HeaderProps) {
  const { hasScrolled, isScrollingUp } = useScrollDirectionNew(50)
  const headerRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname()

  // Layout QR mobile: gallery hero tràn full viewport từ y=0 (không chừa chỗ
  // cho header), header nổi đè lên và tự ẩn trong lúc còn ở vùng ảnh, hiện lại
  // ngay khi cuộn qua khỏi gallery (h-dvh đầu tiên) xuống ProductInfoView.
  const [isInGallerySection, setIsInGallerySection] =
    useState(isQrGalleryLayout)

  useEffect(() => {
    if (!isQrGalleryLayout) return

    const handleScroll = () => {
      setIsInGallerySection(window.scrollY < window.innerHeight - 10)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isQrGalleryLayout])

  const isHeaderHiddenInGallery = isQrGalleryLayout && isInGallerySection

  const [isQuickbuyVisible, setIsQuickbuyVisible] = useState(false)

  useEffect(() => {
    const handleQuickbuyToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ visible: boolean }>
      setIsQuickbuyVisible(Boolean(customEvent.detail?.visible))
    }
    window.addEventListener('quickbuy:toggle', handleQuickbuyToggle)
    return () => {
      window.removeEventListener('quickbuy:toggle', handleQuickbuyToggle)
    }
  }, [])

  const homePath = pathname.replace(/^\/en(?=\/|$)/, '') || '/'
  const storyPageType: TStoryModePageType | null =
    homePath === '/'
      ? 'homepage'
      : homePath === '/nu'
        ? 'homepage_women'
        : homePath === '/nam'
          ? 'homepage_men'
          : null
  const storyItems = useStoryModeData(storyPageType ?? 'homepage')
  // Trang chủ (/, /nu, /nam) có story đang bật → hiện Story Mode trên header mobile
  const showMobileStory =
    !!isMobile && storyPageType !== null && storyItems.length > 0

  const getHeaderHeight = () => {
    let height = 0
    // Story Mode: (Mobile ≈ 70px, Desktop ≈ 100px)
    height += showMobileStory ? 70 : 0
    if (isShowTopBar) height += 32
    if (isShowNavbar) height += isMobile ? 51 : 68
    // Reserve space based on props only — avoids CLS from async homeSetting loading.
    // TopBarPromotion hides itself via CSS when there are no items.
    if (isShowPromotion) height += 34
    return height
  }

  return (
    <>
      <header
        ref={headerRef}
        className={cn(
          'fixed top-[-1] z-50 w-full transition-transform duration-300 lg:top-0',
          (isHeaderHiddenInGallery || isQuickbuyVisible) && '-translate-y-full'
        )}
        aria-label='Site header'
      >
        {isShowTopBar && (
          <TopBar isScrollingDown={hasScrolled && isScrollingUp} />
        )}
        {isShowNavbar && (
          <Navbar
            isMobile={isMobile}
            isTopBarPromotionHidden={hasScrolled && isShowPromotion}
          />
        )}
        {showMobileStory && storyPageType && (
          <div className='bg-white'>
            <StoryMode pageType={storyPageType} />
          </div>
        )}
        {isShowPromotion && <TopBarPromotion isScrollingDown={hasScrolled} />}
      </header>
      {/* Layout QR: bỏ spacer trong lúc header đang ẩn (gallery tràn full
          viewport từ y=0); ngay khi header hiện lại (đã cuộn qua gallery)
          phải trả lại spacer, nếu không header fixed sẽ đè lên ProductInfoView
          suốt phần còn lại của trang. */}
      {!isHeaderHiddenInGallery && (
        <div role='presentation' style={{ height: `${getHeaderHeight()}px` }} />
      )}
    </>
  )
}
